import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState, useRef } from "react";
import { GdkMultipleSelectBox } from "@gdk/multiple-select-box";
import $ from "jquery";
import { classes } from "../../util/style-utils";
import { updateAdditionalInformation } from "../../api/agency-profile";
import { GdkTooltip } from '@gdk/tooltip';
import { v4 } from 'uuid';
import { LANGUAGE_OPTIONS } from "@geicoap/ia-shared";

export default function AdditionalInformationForm({
    agencyProfile = {},
    setAgencyProfile = () => { },
    expandableForm,
    rewardsParticipationLevelSuccessToastRef
}) {
    const [enableButtons, setEnableButtons] = useState(true);

    const { register, handleSubmit, formState: { errors }, reset, watch, setValue, getValues } = useForm({ resolver: yupResolver(validationSchema) });

    const onSubmit = (values) => {
        if (values.incentivesContact !== agencyProfile.marketingIncentives?.incentivesContact) {
            openRewardsParticipationLevelModal();
        }
        else {
            submitForm(values);
        }
    }

    const submitForm = (values, finallyCallback) => {
        if (!values.errors) {
            const associations = GetAssociationsValues();
            const businessRelationships = GetBusinessRelationshipsValues();
            const languages = GetLanguagesValues();

            const updatedAgencyProfile = {
                ...agencyProfile,
                associations: associations,
                maps: businessRelationships,
                businessAddress: {
                    ...agencyProfile.businessAddress,
                    city: values.businessCity
                },
                marketingIncentives: {
                    ...agencyProfile.marketingIncentives,
                    incentivesParticipation: values.incentivesParticipation,
                    incentivesContact: values.incentivesContact
                },
                languages: languages,
                website: values.website?.trim() || '',
            };

            setEnableButtons(false);

            updateAdditionalInformation(updatedAgencyProfile,
                () => {
                    setAgencyProfile(updatedAgencyProfile);
                    expandableForm?.closeExpandedForm(document.getElementById("additional-information-form-li"));
                },
                () => {
                    setEnableButtons(true);
                    finallyCallback?.();
                });
        }
    }

    useEffect(() => { new GdkMultipleSelectBox({ content: "#associations-select" }); }, []);
    useEffect(() => { new GdkMultipleSelectBox({ content: "#business-relationships-select" }); }, []);
    useEffect(() => { new GdkMultipleSelectBox({ content: "#agency-languages-select" }); }, []);
    useEffect(() => { $("#associations-select").trigger("change") });
    useEffect(() => { $("#business-relationships-select").trigger("change") });
    useEffect(() => { $("#agency-languages-select").trigger("change") });

    const associationsOptions = AssociationsOptions();
    const businessRelationshipsOptions = BusinessRelationshipsOptions();
    const incentivesParticipationOptions = IncentivesParticipationOptions();
    const incentivesContactOptions = IncentivesContactOptions();

    const incentivesContactWatch = watch("incentivesContact", String(agencyProfile?.marketingIncentives?.incentivesContact));
    const incentivesParticipationWatch = watch("incentivesParticipation", String(agencyProfile?.marketingIncentives?.incentivesParticipation));

    const onCancel = () => { reset(); };

    const rewardsParticipationLevelModalRef = useRef();
    const openRewardsParticipationLevelModal = () => {
        rewardsParticipationLevelModalRef.current.setAttribute("open", "")
        rewardsParticipationLevelModalRef.current.removeAttribute("overlayshouldclosemodal")
    }
    const closeRewardsParticipationLevelModal = (selection) => {
        setTimeout(() => {
            rewardsParticipationLevelModalRef.current.removeAttribute("open")
        }, 100);

        const values = getValues();
        values.incentivesParticipation = values.incentivesParticipation === "true" || values.incentivesParticipation === true;
        if (selection !== true) {
            setValue("incentivesContact", agencyProfile?.marketingIncentives?.incentivesContact);
            values.incentivesContact = agencyProfile?.marketingIncentives?.incentivesContact;
        }
        else {
            submitForm(values, () => rewardsParticipationLevelSuccessToastRef?.current.setAttribute("visible", ""));
        }
    }

    useEffect(() => {
        if (incentivesParticipationWatch === "true" && incentivesContactWatch === "OPTOUT") {
            setValue("incentivesContact", "");
        }
    }, [incentivesParticipationWatch, incentivesContactWatch]);

    useEffect(() => reset(), [agencyProfile, reset]);

    const rewardsParticipationIdRef = useRef(`tooltip-trigger-${v4()}`);
    const gdkTooltip = useRef(null);
    useEffect(() => {
        gdkTooltip.Current = new GdkTooltip({ content: `#${rewardsParticipationIdRef.current}` });
    }, []);

    const rewardsParticipationToolTip = () => {
        return (
            <>
                <div>
                    Changes to your agency's participation will not impact any currently activated promotions. Future promotion activations will reflect this change.
                </div>
                <button className="icon-close" aria-label="Close tooltip" />
            </>
        );
    }
    const businessRelationshipsToolTip = () => {
        return (
            <>
                <div>
                    To edit your business relationship, please email <a href="mailto:GAO@geico.com">GAO@geico.com</a> and include your Agency G-code and requested business affiliation.
                </div>
                <button className="icon-close" aria-label="Close tooltip" />
            </>
        );
    }

    return (
        <form className="form" onSubmit={handleSubmit(onSubmit)} >
            <div className={classes("form-field", errors.associations && "form-field--error")}>
                <label htmlFor="associations-select" className="text">Associations</label>
                <div>
                    <select multiple id="associations-select" {...register("associations", { value: agencyProfile?.associations })} >
                        <option value=""></option>
                        {associationsOptions.map(option => (
                            <option value={option.value} key={option.value} >{option.text}</option>
                        ))}
                    </select>
                </div>
                {errors.associations &&
                    <span className="form-message">{errors.associations.message}</span>
                }
            </div>

            <div className={classes("form-field", errors.businessRelationships && "form-field--error")}>
                <div className="tooltip-label-container">
                    <label htmlFor="business-relationships-select" className="text">Business Relationships</label>
                    <div className="tooltip-container">
                        <div id={rewardsParticipationIdRef.current} className="tooltip-trigger" data-tooltip-view="tooltip-view-business-relationships-tip" aria-labelledby="label-business-relationships" >
                            <span className="icon-question-mark"></span>
                        </div>
                    </div>
                </div>
                <div>
                    <select id="business-relationships-select" {...register("businessRelationships", { value: agencyProfile?.maps?.map((x) => x.name) })} disabled>
                        <option value=""></option>
                        {businessRelationshipsOptions.map(option => (
                            <option value={option.value} key={option.value} >{option.text}</option>
                        ))}
                    </select>
                </div>
                <div id="tooltip-view-business-relationships-tip" className="tooltip">
                    {businessRelationshipsToolTip()}
                </div>
                {errors.businessRelationships &&
                    <span className="form-message">{errors.businessRelationships.message}</span>
                }
            </div>

            <div className={classes("form-field", errors.businessCity && "form-field--error")}>
                <label htmlFor="business-city" className="text">Business City</label>
                <input id="business-city" type="text" size="30" {...register("businessCity", { value: agencyProfile.businessAddress?.city })} />
                {errors.businessCity &&
                    <span className="form-message">{errors.businessCity.message}</span>
                }
            </div>

            <fieldset role="radiogroup" className={classes("form-field radio-button-group", errors.incentivesParticipation && "form-field--error")}>
                <div className="tooltip-label-container">
                    <legend id="label-rewards-participation" htmlFor="rewards-participation" className="text label--inline">Rewards Participation</legend>
                    <div className="tooltip-container">
                        <div id={rewardsParticipationIdRef.current} className="tooltip-trigger" data-tooltip-view="tooltip-view-rewards-participation-tip" aria-labelledby="label-rewards-participation" >
                            <span className="icon-question-mark"></span>
                        </div>
                    </div>
                </div>
                <div id="tooltip-view-rewards-participation-tip" className="tooltip">
                    {rewardsParticipationToolTip()}
                </div>
                <div className="radio-button-wrapper col-2" >
                    {incentivesParticipationOptions?.map((option) => (
                        <div key={`${option.value}-incentives-participation-radio`}>
                            <input
                                id={`${option.value}-participation-radio`}
                                type="radio"
                                value={option.value}
                                {...register("incentivesParticipation", { value: String(agencyProfile.marketingIncentives?.incentivesParticipation) })}
                            />
                            <label className="radio" htmlFor={`${option.value}-participation-radio`}><span>{option.label}</span></label>
                        </div>
                    ))}
                </div>
                {errors.incentivesParticipation &&
                    <span className="form-message">{errors.incentivesParticipation.message}</span>
                }
            </fieldset>

            {incentivesParticipationWatch === "true" && <div className={classes("form-field", errors.incentivesContact && "form-field--error")}>
                <label htmlFor="incentives-contact-select" className="text">Rewards Participation Level</label>
                <div className="select-box">
                    <select id="incentives-contact-select" {...register("incentivesContact", { value: agencyProfile.marketingIncentives?.incentivesContact })}>
                        {incentivesContactOptions?.filter(option => incentivesParticipationWatch === "false" || option.value !== "OPTOUT")?.map(option => (
                            <option key={`${option.value}-incentives-contact-select`} value={option.value}>{option.text}</option>
                        ))}
                    </select>
                </div>
                {errors.incentivesContact &&
                    <span className="form-message">{errors.incentivesContact.message}</span>
                }
            </div>
            }

            <div className={classes("form-field", errors.languages && "form-field--error")}>
                <label htmlFor="agency-languages-select" className="text">Languages</label>
                <div>
                    <select multiple id="agency-languages-select" {...register("languages", { value: agencyProfile?.languages })} >
                        {LANGUAGE_OPTIONS.map(option => (
                            <option value={option.value} key={option.value} >{option.label}</option>
                        ))}
                    </select>
                </div>
                {errors.languages &&
                    <span className="form-message">{errors.languages.message}</span>
                }
            </div>

            <div className={classes("form-field large-text-input", errors.website && "form-field--error")}>
                <label htmlFor="website" className="text">Website</label>
                <input id="website" type="text" size="30" defaultValue={agencyProfile?.website ?? ""} {...register("website")} />
                {errors.website &&
                    <span className="form-message">{errors.website.message}</span>
                }
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <button
                        type="submit"
                        className="btn btn--primary btn--full-mobile btn--pull-right"
                        disabled={!enableButtons}
                    >
                        <span>Save</span>
                    </button>
                    <button
                        type="button"
                        className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn"
                        onClick={() => onCancel()}
                        disabled={!enableButtons}
                    >
                        <span>Cancel</span>
                    </button>
                </div>
            </div>
            <gds-modal type="dialog" ref={rewardsParticipationLevelModalRef}>
                <span slot="header">Rewards Participation Level</span>
                <div>
                    <p>Changes to your agency's participation level will not impact any currently activated promotions. Future promotion activations will reflect this change.</p>
                </div>
                <gds-button appearance="primary" slot="buttons" onClick={() => { closeRewardsParticipationLevelModal(true) }}>Confirm</gds-button>
                <gds-button appearance="primary" slot="buttons" onClick={() => { closeRewardsParticipationLevelModal(false) }}>Cancel</gds-button>
            </gds-modal>
        </form >
    );
};

const urlReg = /^(https?:\/\/|https?:\/\/(www\.)|(www\.))[-a-zA-Z0-9@:%._\+~#=]+\.[a-zA-Z0-9()]+\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/

const validationSchema = yup.object({
    associations: yup.array().test("at-least-one", "Please select at least one association.", () => GetAssociationsValues().length > 0),
    businessCity: yup.string().matches(/^[A-Za-z ]*$/, "City can only contain letters.").required("Please enter a city."),
    incentivesParticipation: yup.bool().required("Please make a selection."),
    incentivesContact: yup.string().when("incentivesParticipation", {
        is: (value) => value === true,
        then: (schema) => schema.required("Required."),
        otherwise: (schema) => schema.nullable()
    }),
    languages: yup.array().test("at-least-one", "Please select at least one language.", () => GetLanguagesValues().length > 0),
    website: yup.string().nullable().test("is-valid-url", "Please enter a valid URL.", function (value) {
        if (!value) return true; // allow empty
        return urlReg.test(value); // validate only if not empty
    })
});

const AssociationsOptions = () => [
    { value: "NOT", text: "None" },
    { value: "IIABA", text: "IIABA" },
    { value: "PIA", text: "National Association of Professional Insurance Agents" },
    { value: "SIA", text: "State Insurance Association" },
    { value: "OTHER", text: "Other" }
];

const BusinessRelationshipsOptions = () => [
    { value: "ACRI", text: "Acrisure" },
    { value: "AFFAM", text: "Affordable American" },
    { value: "AGTALLSRV", text: "Agents Alliance Services" },
    { value: "AGYCOLL", text: "Agency Collective" },
    { value: "AISI", text: "AIS Insurance" },
    { value: "ALERA", text: "Alera" },
    { value: "ALLEG", text: "Allegiance" },
    { value: "ALLIANT", text: "Alliant" },
    { value: "AMSUI", text: "AmSuisse" },
    { value: "AON", text: "AON" },
    { value: "ARM", text: "ARM Mid-America" },
    { value: "ARMMID", text: "ARM of Mid America" },
    { value: "ARTJG", text: "Arthur J Gallagher" },
    { value: "ASNOA", text: "ASNOA" },
    { value: "ASSURPRT", text: "Assured Partners" },
    { value: "ATLAS", text: "Atlas Insurance Brokers" },
    { value: "BAINS", text: "Bainswest Inc" },
    { value: "BALDRP", text: "Baldwin Risk Partner" },
    { value: "BANDB", text: "Brown & Brown" },
    { value: "BGI", text: "BGI Agency Network" },
    { value: "BRIGHT", text: "Brightway Ins" },
    { value: "BROADP", text: "Broadstreet Partners" },
    { value: "BXS", text: "BXS" },
    { value: "CACACS", text: "Cobbs Allen/CAC Specialty" },
    { value: "CANLNDINS", text: "Canyon Lands Insurance" },
    { value: "CCAGY", text: "Charles Crane Agency" },
    { value: "CBIG", text: "Chambers Bay" },
    { value: "CHOFG", text: "Choice Financial Group LLC" },
    { value: "CIA", text: "Comparion Insurance Agency" },
    { value: "COMBAGTAM", text: "Combined Agents of America (CAA)" },
    { value: "COMPAR", text: "Comparion" },
    { value: "CONFIE", text: "Confie" },
    { value: "CONSOLIN", text: "Consolidated Insurance Nation" },
    { value: "COVERWALL", text: "CoverWallet" },
    { value: "CROSSINS", text: "Cross Insurance" },
    { value: "DAIS", text: "Amwins Direct Access Insurance Services (DAIS)" },
    { value: "DANHEN", text: "The Daniel and Henry Company" },
    { value: "EPIC", text: "EPIC Insurance Brokers and Consultants" },
    { value: "ESTR", text: "Estrella" },
    { value: "FARM", text: "Farmers" },
    { value: "FOUNDRISK", text: "Foundation Risk" },
    { value: "FREEWAY", text: "Freeway Ins Services" },
    { value: "FRSTCHINS", text: "First Choice Ins" },
    { value: "GFLORIDA", text: "GreatFlorida Insurance" },
    { value: "GLOBGRN", text: "Global Green" },
    { value: "GOOSE", text: "Goosehead Insurance" },
    { value: "HEFFIB", text: "Heffernan Insurance Brokers" },
    { value: "HIGHSP", text: "High Street Partners" },
    { value: "HIP", text: "Highstreet Insurance Partners" },
    { value: "HIGGIN", text: "Higginbotham" },
    { value: "HILB", text: "Hilb Group" },
    { value: "HORTON", text: "Horton Group" },
    { value: "HUB", text: "HUB International" },
    { value: "HYLANT", text: "Hylant Group" },
    { value: "IAN", text: "Insurance Agency Network (IAN)" },
    { value: "IIAT", text: "Independent Insurance Agents Of Texas (IIAT)" },
    { value: "IMAFG", text: "IMA Financial Group" },
    { value: "IMS", text: "IMS" },
    { value: "INSOFFAM", text: "Insurance Offices of America (IOA)" },
    { value: "INSURICA", text: "INSURICA Inc" },
    { value: "INSURONE", text: "InsureOne Ins Services" },
    { value: "INTEGRA", text: "Integra Financial Group" },
    { value: "INZONE", text: "Inzone" },
    { value: "IRNPK", text: "Ironpeak" },
    { value: "ISG", text: "ISG United" },
    { value: "ISNE", text: "Insurance Services Of New England (ISNE)" },
    { value: "ISUSTEADY", text: "ISU Steadfast Insurance Agency Network" },
    { value: "ISU", text: "ISU" },
    { value: "KEYS", text: "Keystone" },
    { value: "LAINS", text: "LA Insurance" },
    { value: "LAWLEY", text: "Lawley LLC" },
    { value: "LEAVG", text: "Leavitt Group" },
    { value: "LIBCOMP", text: "The Liberty Company Ins Brokers" },
    { value: "LOCK", text: "Lockton" },
    { value: "MARSHBCP", text: "Marsh Berry Connect Platform" },
    { value: "MARSHMMA", text: "March McLennan Agency (MMA)" },
    { value: "MARSHUSA", text: "Marsh USA" },
    { value: "MEAA", text: "SIAA - Mountain Empire Agency Alliance" },
    { value: "MFCI", text: "Marshberry FirstChoice Insurance" },
    { value: "MIA", text: "Mississippi Insurance Alliance (SIAA)" },
    { value: "MIAA", text: "Midwest Insurance Agency Alliance (MIAA)" },
    { value: "NAVSAV", text: "NAVSAV" },
    { value: "NAII", text: "North American Insurance Alliance (NAII)" },
    { value: "NFAN", text: "North Florida Agents Network (NFAN)" },
    { value: "NFP", text: "NFP" },
    { value: "OAKINS", text: "Oakbridge Insurance" },
    { value: "OAA", text: "One Agents Alliance (OAA)" },
    { value: "OTHER", text: "Other" },
    { value: "PATGROW", text: "Patriot Growth" },
    { value: "PCF", text: "Trucordia" },
    { value: "PCIA", text: "Pacific Crest Insurance" },
    { value: "PIA", text: "Pennsylvania Insurance Alliance (PIA)" },
    { value: "PIASEL", text: "PIA Select" },
    { value: "PIIB", text: "Pacific Interstate Insurance Brokers (PIIB)" },
    { value: "POLLY", text: "Polly" },
    { value: "PREMGI", text: "Premier Group Insurance" },
    { value: "RELII", text: "Relation Insurance Inc" },
    { value: "RENAG", text: "Renaissance Alliance Group" },
    { value: "RICHANDC", text: "Rich and Cartmill" },
    { value: "RMWI", text: "Rocky Mountain West Insurance (RMWI)" },
    { value: "RRASSOC", text: "Robertson Ryan & Associates" },
    { value: "RSCIB", text: "RSC Insurance Brokerage" },
    { value: "SBMP", text: "SBMP Insurance Group" },
    { value: "SCOTTI", text: "Scott Insurance" },
    { value: "SIAAAA", text: "SIAA: The Agent Alliance" },
    { value: "SIMPIOA", text: "Simply IOA" },
    { value: "SNA", text: "Southeastern Network Agencies (SNA)" },
    { value: "SSB", text: "SSB Insurance" },
    { value: "SSBIS", text: "SSB Insurance Services" },
    { value: "STERSP", text: "Sterling Seacrest Pritchard" },
    { value: "SUNSTAR", text: "Sunstar Insurance Group" },
    { value: "SUPIP", text: "Superior Insurance Partners" },
    { value: "TAG", text: "TAG Insurance Services" },
    { value: "TIA", text: "Trusted Insurance Alliance (TIA)" },
    { value: "TMAHONG", text: "The Mahoney Group" },
    { value: "TRUN", text: "True North" },
    { value: "TWFG", text: "TWFG" },
    { value: "UNIV", text: "UniVista" },
    { value: "USI", text: "USI" },
    { value: "WEINS", text: "We Insure Group" },
    { value: "WESTIA", text: "Westwood Insurance Agency" },
    { value: "WILLIS", text: "Willis" },
    { value: "WOODSAW", text: "Woodruff Sawyer" },
    { value: "WRLDIA", text: "World Insurance Associates" }
];

const IncentivesParticipationOptions = () => [
    { value: true, label: "Yes" },
    { value: false, label: "No" }
];

const IncentivesContactOptions = () => [
    { value: "AGY", text: "Agency Only" },
    { value: "PROD", text: "Producers Only" },
    { value: "BOTH", text: "Agency and Producers" },
    { value: "OPTOUT", text: "Opt-out" }
];


const GetAssociationsValues = () => $("select[name=associations]").select2("data").map(d => d.id);
const GetBusinessRelationshipsValues = () => $("select[name=businessRelationships]").select2("data").map(d => ({ name: d.id }));
const GetLanguagesValues = () => $("select[name=languages]").select2("data").map(d => d.id);

export {
    AssociationsOptions,
    IncentivesParticipationOptions,
    IncentivesContactOptions,
    BusinessRelationshipsOptions
};
